import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: 'Mastering Agile Estimation: A Complete Guide',
    excerpt: 'Learn the best practices for story point estimation and how to make your sprint planning sessions more effective.',
    author: 'Alex Johnson',
    date: 'Mar 15, 2024',
    readTime: '5 min read',
    category: 'Agile',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop'
  },
  {
    id: 2,
    title: 'Remote Team Collaboration: Tips & Tricks',
    excerpt: 'Discover how to run effective planning poker sessions with distributed teams and maintain productivity.',
    author: 'Sarah Chen',
    date: 'Mar 12, 2024',
    readTime: '7 min read',
    category: 'Remote Work',
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=400&fit=crop'
  },
  {
    id: 3,
    title: 'Why Story Points Beat Time Estimates',
    excerpt: 'Understanding the psychology behind relative estimation and why it leads to better project outcomes.',
    author: 'Mike Wilson',
    date: 'Mar 10, 2024',
    readTime: '4 min read',
    category: 'Estimation',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop'
  },
  {
    id: 4,
    title: 'Building Consensus in Your Dev Team',
    excerpt: 'How planning poker fosters team discussion and helps arrive at better estimates through collaboration.',
    author: 'Emily Davis',
    date: 'Mar 8, 2024',
    readTime: '6 min read',
    category: 'Team Building',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop'
  },
  {
    id: 5,
    title: 'Common Estimation Pitfalls to Avoid',
    excerpt: 'Learn about the most frequent mistakes teams make during estimation and how to overcome them.',
    author: 'Tom Anderson',
    date: 'Mar 5, 2024',
    readTime: '8 min read',
    category: 'Best Practices',
    image: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=800&h=400&fit=crop'
  },
  {
    id: 6,
    title: 'Scaling Agile: From Small Teams to Enterprise',
    excerpt: 'Strategies for implementing planning poker and agile estimation in large organizations.',
    author: 'Lisa Park',
    date: 'Mar 2, 2024',
    readTime: '10 min read',
    category: 'Enterprise',
    image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=400&fit=crop'
  }
];

const Blog: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-3xl"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-3">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center">
              <div className="relative">
                <div className="w-6 h-8 bg-blue-300 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">5</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
            <div className="absolute -right-1 -bottom-1 w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center -rotate-6">
              <div className="relative">
                <div className="w-6 h-8 bg-gray-800 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-2xl">1</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          <span className="text-xl font-medium text-white ml-8 cursor-pointer" onClick={() => navigate('/')}>Scrumo</span>
        </div>

        {/* Nav Links */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full border border-white/30 transition-colors">
            Home
          </button>
          <button className="px-4 py-2 text-sm text-white bg-white/20 rounded-full border border-white/30">
            Blog
          </button>
          <button onClick={() => navigate('/faq')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full transition-colors">
            FAQ
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 px-8 py-12 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Insights & <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Best Practices</span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Learn from industry experts about agile estimation, team collaboration, and making the most of your planning sessions.
          </p>
        </div>

        {/* Featured Post */}
        <div className="mb-16">
          <div className="relative group cursor-pointer overflow-hidden rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 hover:border-white/30 transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative grid md:grid-cols-2 gap-8 p-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 backdrop-blur-xl rounded-full border border-purple-500/30">
                  <span className="text-xs font-semibold text-purple-200">Featured</span>
                </div>
                <h2 className="text-3xl font-bold text-white">
                  The Ultimate Guide to Planning Poker in 2024
                </h2>
                <p className="text-lg text-purple-200 line-clamp-3">
                  Everything you need to know about running effective estimation sessions, from basic concepts to advanced techniques used by top agile teams worldwide.
                </p>
                <div className="flex items-center gap-4 text-sm text-purple-300">
                  <span>By Alex Johnson</span>
                  <span>•</span>
                  <span>Mar 20, 2024</span>
                  <span>•</span>
                  <span>12 min read</span>
                </div>
                <Button className="bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600">
                  Read Article
                </Button>
              </div>
              <div className="relative h-64 md:h-auto">
                <img
                  src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=600&fit=crop"
                  alt="Featured post"
                  className="absolute inset-0 w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="group cursor-pointer bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden hover:border-white/30 hover:transform hover:scale-105 transition-all duration-300"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-xl rounded-full text-xs font-semibold text-white border border-white/30">
                    {post.category}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-white line-clamp-2 group-hover:text-purple-200 transition-colors">
                  {post.title}
                </h3>
                <p className="text-purple-200 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-purple-300">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full"></div>
                    <span>{post.author}</span>
                  </div>
                  <span>{post.readTime}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-purple-400">
                  <span>{post.date}</span>
                  <span className="group-hover:text-purple-200 transition-colors">Read more →</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="mt-24 relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-xl border border-white/20 p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>

          <div className="relative text-center max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold text-white">
              Stay Updated with Agile Insights
            </h2>
            <p className="text-lg text-purple-200">
              Get weekly tips on estimation, team collaboration, and agile best practices delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-purple-200/50 focus:outline-none focus:border-white/50"
              />
              <Button className="bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600 px-8">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
